import { useState } from "react";
import { api } from "../../lib/api";
import { Loader2, RefreshCw, CheckCircle2, AlertTriangle, XCircle, ChevronDown, ExternalLink } from "lucide-react";
import { toast } from "sonner";

/**
 * Admin → SEO analysis.
 *
 * Two tools in one page:
 *   1. Site-wide scan (POST /api/admin/seo/scan) - server-side fetches each
 *      public route, parses the HTML, scores it 0-100 with checks.
 *   2. Article pre-publish score (POST /api/admin/seo/score-article) - paste
 *      a draft article and get the same heuristics applied to it.
 */
const STATUS_STYLES = {
  pass: { icon: CheckCircle2, color: "text-[#0d9b80]", bg: "bg-[#71edc9]/25", label: "Pass" },
  warn: { icon: AlertTriangle, color: "text-amber-600", bg: "bg-amber-50", label: "Warn" },
  fail: { icon: XCircle, color: "text-red-600", bg: "bg-red-50", label: "Fail" },
};

function ScoreBadge({ score }) {
  const ring = score >= 80 ? "border-[#0d9b80] text-[#0d9b80]"
    : score >= 60 ? "border-amber-500 text-amber-600"
    : "border-red-500 text-red-600";
  return (
    <div className={`inline-flex items-center justify-center h-14 w-14 rounded-2xl border-4 ${ring} bg-white font-display font-bold text-lg`}>
      {score}
    </div>
  );
}

function ChecksList({ checks }) {
  return (
    <ul className="space-y-2">
      {checks.map((c, i) => {
        const S = STATUS_STYLES[c.status] || STATUS_STYLES.warn;
        const Icon = S.icon;
        return (
          <li key={i} className={`rounded-2xl p-3 flex items-start gap-3 ${S.bg}`}>
            <Icon size={18} className={`${S.color} shrink-0 mt-0.5`} />
            <div className="flex-1 min-w-0">
              <div className="flex items-baseline justify-between gap-3 flex-wrap">
                <span className="font-display font-semibold text-[#04273d] text-sm">{c.label}</span>
                <span className="text-xs text-[#447694] font-mono-brand">{c.value}</span>
              </div>
              {c.recommendation && (
                <p className="text-xs text-[#04273d] mt-1.5 leading-relaxed">{c.recommendation}</p>
              )}
            </div>
          </li>
        );
      })}
    </ul>
  );
}

function PageResultCard({ result, baseUrl, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="bnp-card overflow-hidden" data-testid={`seo-result-${result.url.replace(/[^a-z0-9]+/gi, '-')}`}>
      <button onClick={() => setOpen(!open)} className="w-full p-5 flex items-start gap-4 text-left">
        <ScoreBadge score={result.score} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-display font-bold text-[#04273d] truncate">{result.url}</span>
            {baseUrl && (
              <a
                href={baseUrl + result.url}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="text-[#447694] hover:text-[#04273d]"
              >
                <ExternalLink size={12} />
              </a>
            )}
          </div>
          <p className="text-sm text-[#447694] mt-1 truncate">{result.title || "(no title tag)"}</p>
          <p className="text-xs text-[#447694] mt-1">
            {result.checks.filter((c) => c.status === "fail").length} failures ·
            {" "}{result.checks.filter((c) => c.status === "warn").length} warnings ·
            {" "}{result.checks.filter((c) => c.status === "pass").length} passes
          </p>
        </div>
        <ChevronDown size={20} className={`text-[#447694] transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="px-5 pb-5 border-t border-[#9ac7f7]/20 pt-4">
          <ChecksList checks={result.checks} />
        </div>
      )}
    </div>
  );
}

export default function AdminSeo() {
  const [tab, setTab] = useState("site");

  // Site scan state
  const [scanning, setScanning] = useState(false);
  const [scan, setScan] = useState(null);

  // Article scoring state
  const [article, setArticle] = useState({
    title: "",
    slug: "",
    content_md: "",
    meta_description: "",
    focus_keyword: "",
  });
  const [articleScore, setArticleScore] = useState(null);
  const [articleScoring, setArticleScoring] = useState(false);

  const runScan = async () => {
    setScanning(true);
    try {
      // Pass the admin browser's public origin so the backend scans the same
      // URL the customer sees, not the internal cluster URL.
      const { data } = await api.post("/admin/seo/scan", { base_url: window.location.origin });
      setScan(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not run SEO scan.");
    } finally {
      setScanning(false);
    }
  };

  const scoreArticle = async () => {
    if (!article.title.trim()) {
      toast.error("Add a title to score this article.");
      return;
    }
    setArticleScoring(true);
    try {
      const { data } = await api.post("/admin/seo/score-article", article);
      setArticleScore(data);
    } catch (e) {
      toast.error("Could not score article.");
    } finally {
      setArticleScoring(false);
    }
  };

  return (
    <div className="p-6 md:p-10" data-testid="admin-seo-page">
      <div>
        <span className="bnp-chip bnp-chip-light">SEO analysis</span>
        <h1 className="font-display text-3xl md:text-4xl text-[#04273d] mt-2 font-semibold tracking-tight">SEO health</h1>
        <p className="text-sm text-[#447694] mt-1 max-w-2xl">
          Scan the live site for SEO health, and score blog drafts before they publish. Heuristic checks - fast, free, no external API needed.
        </p>
      </div>

      {/* Tabs */}
      <div className="mt-6 flex items-center gap-1 border-b border-[#9ac7f7]/30" role="tablist">
        <button
          onClick={() => setTab("site")}
          role="tab"
          aria-selected={tab === "site"}
          className={`px-4 py-2 -mb-px border-b-2 font-display font-semibold text-sm transition-colors ${tab === "site" ? "border-[#04273d] text-[#04273d]" : "border-transparent text-[#447694] hover:text-[#04273d]"}`}
          data-testid="seo-tab-site"
        >
          Site scan
        </button>
        <button
          onClick={() => setTab("article")}
          role="tab"
          aria-selected={tab === "article"}
          className={`px-4 py-2 -mb-px border-b-2 font-display font-semibold text-sm transition-colors ${tab === "article" ? "border-[#04273d] text-[#04273d]" : "border-transparent text-[#447694] hover:text-[#04273d]"}`}
          data-testid="seo-tab-article"
        >
          Score an article
        </button>
      </div>

      {/* SITE SCAN TAB */}
      {tab === "site" && (
        <div className="mt-6 space-y-5">
          <div className="bnp-card p-5 flex items-center justify-between gap-4 flex-wrap">
            <div>
              <p className="font-display font-bold text-[#04273d]">Scan public pages</p>
              <p className="text-sm text-[#447694] mt-1">
                Fetches every key route + up to 8 published blog posts. Takes a few seconds.
              </p>
            </div>
            <button onClick={runScan} disabled={scanning} className="btn-primary text-sm" data-testid="seo-run-scan-button">
              {scanning ? <><Loader2 size={14} className="animate-spin" /> Scanning…</> : <><RefreshCw size={14} /> Run scan</>}
            </button>
          </div>

          {scan && (
            <>
              <div className="bnp-card p-6 flex items-center gap-5" data-testid="seo-scan-summary">
                <ScoreBadge score={scan.average_score} />
                <div className="flex-1 min-w-0">
                  <p className="font-display font-bold text-[#04273d] text-xl">Average score across {scan.results.length} pages</p>
                  <p className="text-sm text-[#447694] mt-1">
                    Last scanned {new Date(scan.scanned_at).toLocaleString("en-GB")} · {scan.base_url}
                  </p>
                </div>
              </div>

              <div className="space-y-3">
                {scan.results
                  .slice()
                  .sort((a, b) => a.score - b.score)
                  .map((r, i) => (
                    <PageResultCard key={r.url} result={r} baseUrl={scan.base_url} defaultOpen={i === 0} />
                  ))}
              </div>
            </>
          )}

          {!scan && !scanning && (
            <div className="bnp-card p-10 text-center text-[#447694] text-sm">
              Press <strong className="text-[#04273d]">Run scan</strong> to analyse the public pages.
            </div>
          )}
        </div>
      )}

      {/* ARTICLE SCORE TAB */}
      {tab === "article" && (
        <div className="mt-6 grid lg:grid-cols-2 gap-6">
          <div className="bnp-card p-6 space-y-4" data-testid="seo-article-form">
            <div>
              <label className="block text-xs font-display font-semibold text-[#447694] tracking-widest uppercase mb-1.5">Title</label>
              <input
                value={article.title}
                onChange={(e) => setArticle({ ...article, title: e.target.value })}
                placeholder="How to protect your UK business name"
                className="bnp-input"
                data-testid="seo-article-title"
              />
            </div>
            <div>
              <label className="block text-xs font-display font-semibold text-[#447694] tracking-widest uppercase mb-1.5">Slug</label>
              <input
                value={article.slug}
                onChange={(e) => setArticle({ ...article, slug: e.target.value })}
                placeholder="how-to-protect-your-uk-business-name"
                className="bnp-input font-mono-brand"
                data-testid="seo-article-slug"
              />
            </div>
            <div>
              <label className="block text-xs font-display font-semibold text-[#447694] tracking-widest uppercase mb-1.5">Focus keyword</label>
              <input
                value={article.focus_keyword}
                onChange={(e) => setArticle({ ...article, focus_keyword: e.target.value })}
                placeholder="business name protection"
                className="bnp-input"
                data-testid="seo-article-keyword"
              />
            </div>
            <div>
              <label className="block text-xs font-display font-semibold text-[#447694] tracking-widest uppercase mb-1.5">Meta description</label>
              <textarea
                value={article.meta_description}
                onChange={(e) => setArticle({ ...article, meta_description: e.target.value })}
                placeholder="140-160 characters that summarise the article and entice clicks."
                rows={3}
                className="bnp-input"
                data-testid="seo-article-meta"
              />
            </div>
            <div>
              <label className="block text-xs font-display font-semibold text-[#447694] tracking-widest uppercase mb-1.5">Content (markdown)</label>
              <textarea
                value={article.content_md}
                onChange={(e) => setArticle({ ...article, content_md: e.target.value })}
                placeholder={"## Why this matters\n\nYour article body here. Use **markdown** for headings."}
                rows={14}
                className="bnp-input font-mono-brand text-sm"
                data-testid="seo-article-content"
              />
            </div>
            <button onClick={scoreArticle} disabled={articleScoring} className="btn-primary w-full justify-center" data-testid="seo-score-article-button">
              {articleScoring ? <><Loader2 size={14} className="animate-spin" /> Scoring…</> : <>Score this article</>}
            </button>
          </div>

          <div data-testid="seo-article-result">
            {articleScore ? (
              <div className="bnp-card p-6 sticky top-6">
                <div className="flex items-center gap-4">
                  <ScoreBadge score={articleScore.score} />
                  <div className="flex-1 min-w-0">
                    <p className="font-display font-bold text-[#04273d] text-xl">SEO score</p>
                    <p className="text-sm text-[#447694] mt-1 truncate">{articleScore.title || "(no title)"}</p>
                  </div>
                </div>
                <div className="mt-5">
                  <ChecksList checks={articleScore.checks} />
                </div>
              </div>
            ) : (
              <div className="bnp-card p-10 text-center text-[#447694] text-sm sticky top-6">
                Fill in the form and press <strong className="text-[#04273d]">Score this article</strong>.
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
